<script lang="ts" setup>
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { USState, ValidationRules } from '@Shared';
import { GuidedShowcaseLayout, ActionBarLayout, US_STATES, useFormValidation, useFieldStatus } from '@Shared';
import useFlowNavigation from '@Use/useFlowNavigation';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useKbaStore } from '@Stores/kba.store';
import useCompanyImageBaseUrl from '@Src/use/useCompanyImageBaseUrl';
import ShowcaseImage from '@Components/ShowcaseImage.vue';
import type { KbaUserInfo } from '@Types/kba.types';
import { trackClientKbaQuestionsRetrieved } from '@Analytics/kba.mixpanel';

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const kbaStore = useKbaStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();
const { advanceFlow } = useFlowNavigation();
const router = useRouter();
const route = useRoute();
const context = (route.query.context as string) ?? '';

const form = ref<KbaUserInfo>({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    dateOfBirth: '',
    ssn: '',
});
const selectedState = ref<USState | null>(null);
const isSubmitting = ref(false);

const validationRules = ref<ValidationRules>({
    'address1': [{ required: true, message: 'Address is required' }],
    'city': [{ required: true, message: 'City is required' }],
    'state': [{ required: true, message: 'State is required' }],
    'zip': [
        { required: true, message: 'Zip code is required' },
        { validateZipCode: true, message: 'Valid U.S. zip code is required' }
    ],
    'dateOfBirth': [
        { required: true, message: 'Date of birth is required' },
        { validatePastDate: true, message: 'Please provide a valid date' },
    ],
    'ssn': [
        { validateSsn: true, message: 'Please enter a valid 9-digit Social Security Number' }
    ],
});

const formValidation = useFormValidation(form, validationRules);
const { turnOnValidation, hasError } = formValidation;
const { getFieldStatus, getFieldMessage } = useFieldStatus(formValidation);

const updateState = (state: USState) => {
    form.value.state = state.abbreviation;
};

// Browser/OS address autofill (iOS Safari AutoFill, Chrome Autofill) can only
// write into a native form control. The visible state field is a div-based
// AppDropdown, so we bridge it with a visually-hidden native <select> that
// carries the canonical autocomplete token. When autofill fires, we look up
// the matching US_STATES entry and reuse the AppDropdown's existing change
// path so form state + validation stay in sync.
//
// Match both abbreviation and full name — iOS / Chrome write the option's
// `value`, but some password managers write the full state name and rely on
// the browser's option-text matching. Re-route through updateState() either
// way so validation stays in sync.
const onNativeStateAutofill = (event: Event) => {
    const written = (event.target as HTMLSelectElement).value;
    if (!written) return;
    const writtenLower = written.toLowerCase();
    if (writtenLower === selectedState.value?.abbreviation.toLowerCase()) return;
    const match = US_STATES.find(
        s => s.abbreviation.toLowerCase() === writtenLower || s.name.toLowerCase() === writtenLower
    );
    if (match) {
        selectedState.value = match;
        updateState(match);
    }
};

const submit = async () => {
    await turnOnValidation(true);
    if (hasError.value) return;

    isSubmitting.value = true;
    try {
        await kbaStore.fetchQuestions(form.value);

        if (kbaStore.failed) {
            router.push({ name: 'kba-error' });
            return;
        }

        if (kbaStore.questions.length > 0) {
            trackClientKbaQuestionsRetrieved(context, true, Boolean(form.value.ssn));
        }
        await advanceFlow();
    }
    finally {
        isSubmitting.value = false;
    }
};
</script>

<template>
    <guided-showcase-layout
        suppress-back-to-dashboard
        title="Enter your details"
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">

        <app-brand-loader v-if="isSubmitting" class="loader">
            Retrieving your questions...
            <template #caption>This may take a few seconds, please do not refresh this page</template>
        </app-brand-loader>

        <app-form-content-layout v-if="!isSubmitting">
            <p>Please use an address you have lived at for at least 3 months.</p>

            <app-text-input
                auto-focus
                is-required
                label="Address line 1"
                v-model="form.address1"
                :maxlength="80"
                :field-status="getFieldStatus('address1')"
                :message="getFieldMessage('address1')"
                data-validation-field="address1"
                data-id="kba-address-1" />

            <app-text-input
                v-model="form.address2"
                label="Apartment, suite, etc."
                :maxlength="50"
                data-id="kba-address-2" />

            <app-text-input
                v-model="form.city"
                label="City"
                :maxlength="75"
                is-required
                :field-status="getFieldStatus('city')"
                :message="getFieldMessage('city')"
                data-validation-field="city"
                data-id="kba-city" />

            <div class="state-field">
                <app-dropdown
                    v-model="selectedState"
                    open-up
                    :options="US_STATES"
                    id-field="abbreviation"
                    display-field="name"
                    label="State"
                    is-required
                    auto-open
                    @update:modelValue="updateState"
                    :field-status="getFieldStatus('state')"
                    :message="getFieldMessage('state')"
                    data-validation-field="state"
                    data-id="kba-state" />
                <select
                    name="state"
                    autocomplete="address-level1"
                    aria-hidden="true"
                    tabindex="-1"
                    class="state-autofill-mirror"
                    :value="selectedState?.abbreviation ?? ''"
                    @change="onNativeStateAutofill"
                    @input="onNativeStateAutofill">
                    <option value=""></option>
                    <option
                        v-for="s in US_STATES"
                        :key="s.abbreviation"
                        :value="s.abbreviation">
                        {{ s.name }}
                    </option>
                </select>
            </div>

            <app-masked-input
                v-model="form.zip"
                label="Zip code"
                :maxlength="5"
                mask="#####"
                is-required
                input-mode="numeric"
                :field-status="getFieldStatus('zip')"
                :message="getFieldMessage('zip')"
                data-validation-field="zip"
                data-id="kba-zip" />

            <app-divider />

            <app-masked-input
                v-model="form.dateOfBirth"
                mask-value
                label="Date of birth"
                mask="##/##/####"
                placeholder="mm/dd/yyyy"
                :maxlength="8"
                input-mode="numeric"
                is-required
                :field-status="getFieldStatus('dateOfBirth')"
                :message="getFieldMessage('dateOfBirth')"
                data-validation-field="dateOfBirth"
                data-id="kba-dob" />

            <app-divider />

            <app-masked-input
                v-model="form.ssn"
                mask-value
                label="Social Security Number"
                mask="###-##-####"
                placeholder="***_**_****"
                :maxlength="9"
                input-mode="numeric"
                :field-status="getFieldStatus('ssn')"
                :message="getFieldMessage('ssn')"
                data-validation-field="ssn"
                data-id="kba-ssn">
            </app-masked-input>

            <app-collapse class="dob-ssn-info" initial-state="closed">
                <template #trigger="{ isOpen } = {}">
                    <button
                        type="button"
                        class="dob-ssn-info__toggle"
                        :aria-expanded="isOpen"
                        data-id="kba-dob-ssn-info-toggle">
                        Why do I need to provide my DOB &amp; SSN
                        <span class="dob-ssn-info__arrow" :class="{ 'dob-ssn-info__arrow--open': isOpen }">&#8250;</span>
                    </button>
                </template>
                <p class="dob-ssn-info__body">
                    Providing your Date of Birth helps us gather the most relevant questions in order to verify your identity accurately.
                    Providing your Social Security Number is optional, but it speeds up our validation process especially if you have a common name or share a name with a family member.
                    All information entered in this form is encrypted and never stored by CertifID.
                    For details on how we handle data privacy, please refer to our
                    <a href="https://certifid.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    and
                    <a href="https://certifid.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a>.
                </p>
            </app-collapse>

        </app-form-content-layout>

        <action-bar-layout v-if="!isSubmitting">
            <app-button
                size="large"
                @onClick="submit"
                :is-loading="isSubmitting"
                data-id="kba-details-continue">
                Continue
            </app-button>
        </action-bar-layout>

        <template #branded>
            <showcase-image />
        </template>
    </guided-showcase-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.loader {
    margin-top: 5rem;
}

.state-field {
    position: relative;
}

// Hidden native <select> that picks up browser/OS address autofill on
// behalf of the visible AppDropdown above it. Positioned off-screen
// rather than visually-hidden — iOS Safari's autofill skips fields that
// look "too invisible" (opacity:0 + pointer-events:none + clip-path
// stacked together is read as "fake field"). Off-screen positioning
// keeps it real-to-autofill while invisible to sight.
.state-autofill-mirror {
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
}

.dob-ssn-info {
    margin-top: 0.5rem;

    &__toggle {
        display: flex;
        align-items: center;
        gap: 0.375rem;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        color: $theme-primary;
        font-size: 1rem;
        font-family: inherit;
        text-align: left;
    }

    &__arrow {
        font-size: 1.25rem;
        line-height: 1;
        transition: transform 0.2s ease;
        display: inline-block;
        transform: rotate(90deg);

        &--open {
            transform: rotate(-90deg);
        }
    }

    &__body {
        margin-top: 0.75rem;
        font-size: 0.9rem;
        color: $black-med;
        line-height: 1.5;
    }
}
</style>
